import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, Button, Table, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AdminService } from "services/AdminService";
import { UserTypes } from "enum/UserTypes";
import { User } from "models/User";
import ReactPaginate from "react-paginate";
import Select from "react-select";
import Swal from "sweetalert2";
import Switch from "react-switch";
import {
  useTable,
  useGlobalFilter,
  usePagination,
  Column,
  TableInstance,
  UsePaginationInstanceProps,
  UseGlobalFiltersInstanceProps,
  Row,
  TableState
} from "react-table";
import { VerifiedStatus } from "enum/VerifyRole";
import { PaymentOption } from "enum/PaymentOption";
import CustomerView from "./CustomerView";

type FilterType = 'personal' | 'business' | 'monthly' | 'deactive' | 'mobileUsers';

export interface CustomerViewProps {
  isOpen: boolean;
  handlePopup: () => void;
  filterType: FilterType;
  getAllCustomers: () => void;
  onPageChange?: (selectedPage: { selected: number }) => void;
  onCustomerUpdated?: () => void;
}


type CustomColumn = Column<User> & {
  Cell?: (props: { row: Row<User> }) => React.ReactNode;
};


interface CustomTableInstance extends TableInstance<User>,
  UsePaginationInstanceProps<User>,
  UseGlobalFiltersInstanceProps<User> { }

const CustomerFilterData: React.FC<CustomerViewProps> = ({
  isOpen,
  handlePopup: handleParentPopup,
  filterType,
  getAllCustomers,
  onPageChange,
  onCustomerUpdated
}) => {
  const [selectedCustomer, setSelectedCustomer] = useState<User | null>(null);
  const [customers, setCustomers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [globalFilter, setGlobalFilter] = useState('');
  const [editModal, setEditModal] = useState(false);

  const pageSizeOptions = useMemo(() => [
    { value: 10, label: "10 Rows" },
    { value: 15, label: "15 Rows" },
    { value: 20, label: "20 Rows" },
    { value: 25, label: "25 Rows" },
    { value: 50, label: "50 Rows" },
  ], []);

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      let response;
      const pageNumber = currentPage + 1;

      switch (filterType) {
        case 'personal':
          response = await AdminService.getPersonalCustomerList(tablePageSize, pageNumber);
          break;
        case 'business':
          response = await AdminService.getBusinessCustomerList(tablePageSize, pageNumber);
          break;
        case 'monthly':
          response = await AdminService.getMonthlyCustomerList(tablePageSize, pageNumber);
          break;
        case 'deactive':
          response = await AdminService.getDeactiveCustomerList(tablePageSize, pageNumber);
          break;
        case 'mobileUsers':
          response = await AdminService.getMobileUsersList(tablePageSize, pageNumber);
          break;
        default:
          throw new Error('Invalid filter type');
      }

      if (response?.success) {
        setCustomers(response.data?.userList || []);
        setPageCount(Math.ceil((response.data?.count) / tablePageSize));
      } else {
        setCustomers([]);
        setPageCount(0);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error fetching customers");
      setCustomers([]);
      setPageCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [filterType, currentPage, tablePageSize]);

  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
    }
  }, [isOpen, fetchCustomers]);

  const handleEdit = (customer: User) => {
    setSelectedCustomer(customer);
    setEditModal(true);
  };

  const handleEditModalClose = () => {
    setEditModal(false);
    setSelectedCustomer(null);
  };

  const columns = useMemo<CustomColumn[]>(() => {
    const baseColumns: CustomColumn[] = [
      {
        Header: 'Customer ID',
        accessor: 'customerId',
      },
      {
        Header: 'Customer / Company Name',
        accessor: 'firstName',
        Cell: ({ row }: any) => `${row.original.firstName} ${row.original.lastName}`,
      },
      {
        Header: 'Invoice Email',
        accessor: 'email',
      },
      {
        Header: 'Customer Type',
        accessor: 'role',
        Cell: ({ row }: any) => row.original.role === UserTypes.BUSINESS_CUSTOMER ? 'Business' : 'Personal',
      },
    ];

    // Add Action column only when NOT mobileUsers
    if (filterType !== 'mobileUsers') {
      baseColumns.push({
        id: "action",
        Header: "Action",
        Cell: ({ row }: any) => {
          return (
            <div className="text-center">
              <button
                className="btn btn-primary btn-sm px-3"
                onClick={() => handleEdit(row.original)}
              >
                Edit
              </button>
            </div>
          );
        },
      },);
    }

    return baseColumns;
  }, [filterType]);

  const data = useMemo(() => customers, [customers]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    setGlobalFilter: setTableGlobalFilter,
    page,
    setPageSize,
  } = useTable(
    {
      columns,
      data,
      initialState: {},
    },
    useGlobalFilter,
    usePagination
  ) as CustomTableInstance;

  const handlePageChange = useCallback((selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected);
    if (onPageChange) {
      onPageChange(selectedItem);
    }
  }, [onPageChange]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
    setTableGlobalFilter(value);
  }, [setTableGlobalFilter]);

  const handlePageSizeChange = useCallback((selectedOption: { value: number; label: string } | null) => {
    if (selectedOption) {
      const newSize = selectedOption.value;
      setTablePageSize(newSize);
      setPageSize(newSize);
      setCurrentPage(0);
    }
  }, [setPageSize]);

  const getModalTitle = useCallback(() => {
    const count = customers.length;
    switch (filterType) {
      case 'personal':
        return `Personal Customers`;
      case 'business':
        return `Business Customers`;
      case 'monthly':
        return `Monthly Invoice Customers`;
      case 'deactive':
        return `Deactive Customers`;
      case 'mobileUsers':
        return `Mobile Users`
      default:
        return 'Customers';
    }
  }, [filterType, customers.length]);

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} toggle={handleParentPopup} size="xl" backdrop="static">
        <ModalHeader toggle={handleParentPopup}>
          {getModalTitle()}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-between align-items-end mb-3 flex-wrap gap-2">
            <input
              type="text"
              value={globalFilter}
              onChange={handleSearchChange}
              placeholder="Search customers..."
              className="form-control w-auto"
              style={{ minWidth: "250px" }}
            />

            <div className="d-flex align-items-center">
              <Select
                value={pageSizeOptions.find(option => option.value === tablePageSize)}
                onChange={handlePageSizeChange}
                options={pageSizeOptions}
                className="w-auto"
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: '100px'
                  })
                }}
              />
            </div>
          </div>

          <div className="table-responsive">
            <Table hover {...getTableProps()}>
              <thead>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th {...column.getHeaderProps()}>
                        {column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.length > 0 ? (
                  page.map(row => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map(cell => (
                          <td {...cell.getCellProps()}>
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-4">
                      No customers found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {pageCount > 1 && (
            <ReactPaginate
              pageCount={pageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={handlePageChange}
              forcePage={currentPage}
              containerClassName="pagination justify-content-center mt-3"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              breakClassName="page-item"
              breakLinkClassName="page-link"
              activeClassName="active"
            />
          )}
        </ModalBody>
      </Modal>

      {/* Edit Customer Modal */}
      {selectedCustomer && (
        <CustomerView
          isOpen={editModal}
          handlePopup={handleEditModalClose}
          customer={selectedCustomer}
          getAllCustomers={() => {
            fetchCustomers(); // Refresh the customer list
            if (onCustomerUpdated) { // Use the destructured prop directly
              onCustomerUpdated(); // Notify parent that a customer was updated
            }
          }}
        />
      )}
    </>
  );
};

export default CustomerFilterData;